import React from 'react';
import {
  Box,
  Flex,
  TextField,
  Select,
  Table,
  TablePagination,
  ColumnConfig,
  CellText,
  HeaderPage,
} from '@backstage/ui';
import { RiSearchLine } from '@remixicon/react';
import { Content } from '@backstage/core-components';
import { attachComponentData, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogPlugin } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityTextFilter,
  EntityKindFilter,
  EntityTypeFilter,
  EntityOwnerFilter,
  useEntityList,
  catalogApiRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { GetEntityFacetsResponse } from '@backstage/catalog-client';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { appTranslationRef } from '../../../core/i18n';
import styles from './catalog.page.module.css';

const DEFAULT_KIND = 'System';

// ============================================================================
// Filter Components
// ============================================================================

const KindFilter = () => {
  const catalogApi = useApi(catalogApiRef);
  const { queryParameters, updateFilters } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);
  const [kinds, setKinds] = React.useState<string[]>([]);

  // Read initial kind from URL (queryParameters), default to System
  const kindFromUrl = React.useMemo(
    () => [queryParameters.kind].flat().filter(Boolean)[0] as string | undefined,
    [queryParameters.kind],
  );
  const [selectedKind, setSelectedKind] = React.useState(kindFromUrl ?? DEFAULT_KIND);

  // Sync URL → local state (browser back/forward)
  React.useEffect(() => {
    const newKind = kindFromUrl ?? DEFAULT_KIND;
    setSelectedKind(newKind);
  }, [kindFromUrl]);

  React.useEffect(() => {
    catalogApi
      .getEntityFacets({ facets: ['kind'] })
      .then((response: GetEntityFacetsResponse) => {
        const kindFacet = response.facets.kind || [];
        setKinds(kindFacet.map(f => f.value));
      });
  }, [catalogApi]);

  // Sync local state → EntityListProvider (provider handles URL sync)
  React.useEffect(() => {
    updateFilters({
      kind: selectedKind
        ? new EntityKindFilter(selectedKind, selectedKind)
        : undefined,
    });
  }, [selectedKind, updateFilters]);

  const options = [
    { value: '', label: t('catalog.filters.all') },
    ...kinds.map(kind => ({ value: kind, label: kind })),
  ];

  return (
    <Select
      key={`kind-${selectedKind}`}
      label={t('catalog.filters.kind')}
      options={options}
      defaultValue={selectedKind}
      onChange={value => setSelectedKind(value as string)}
    />
  );
};

const TypeFilter = () => {
  const catalogApi = useApi(catalogApiRef);
  const { queryParameters, updateFilters } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);
  const [types, setTypes] = React.useState<string[]>([]);

  const typeFromUrl = React.useMemo(
    () => {
      const raw = queryParameters.type;
      return (Array.isArray(raw) ? raw[0] : raw) as string | undefined;
    },
    [queryParameters.type],
  );
  const [selectedType, setSelectedType] = React.useState(typeFromUrl ?? '');

  React.useEffect(() => {
    setSelectedType(typeFromUrl ?? '');
  }, [typeFromUrl]);

  React.useEffect(() => {
    catalogApi
      .getEntityFacets({ facets: ['spec.type'] })
      .then((response: GetEntityFacetsResponse) => {
        const typeFacet = response.facets['spec.type'] || [];
        setTypes(typeFacet.map(f => f.value));
      });
  }, [catalogApi]);

  React.useEffect(() => {
    updateFilters({
      type: selectedType ? new EntityTypeFilter(selectedType) : undefined,
    });
  }, [selectedType, updateFilters]);

  const options = [
    { value: '', label: t('catalog.filters.all') },
    ...types.map(type => ({ value: type, label: type })),
  ];

  return (
    <Select
      key={`type-${selectedType}`}
      label={t('catalog.filters.type')}
      options={options}
      defaultValue={selectedType}
      onChange={value => setSelectedType(value as string)}
    />
  );
};

const OwnerFilter = () => {
  const catalogApi = useApi(catalogApiRef);
  const { queryParameters, updateFilters } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);
  const [owners, setOwners] = React.useState<string[]>([]);

  const ownerFromUrl = React.useMemo(
    () => [queryParameters.owners].flat().filter(Boolean)[0] as string | undefined,
    [queryParameters.owners],
  );
  const [selectedOwner, setSelectedOwner] = React.useState(ownerFromUrl ?? '');

  React.useEffect(() => {
    setSelectedOwner(ownerFromUrl ?? '');
  }, [ownerFromUrl]);

  React.useEffect(() => {
    catalogApi
      .getEntityFacets({ facets: ['spec.owner'] })
      .then((response: GetEntityFacetsResponse) => {
        const ownerFacet = response.facets['spec.owner'] || [];
        setOwners(ownerFacet.map(f => f.value));
      });
  }, [catalogApi]);

  React.useEffect(() => {
    updateFilters({
      owners: selectedOwner
        ? new EntityOwnerFilter([selectedOwner])
        : undefined,
    });
  }, [selectedOwner, updateFilters]);

  const options = [
    { value: '', label: t('catalog.filters.all') },
    ...owners.map(owner => ({
      value: owner,
      label: parseEntityRef(owner, { defaultKind: 'group', defaultNamespace: 'default' }).name,
    })),
  ];

  return (
    <Select
      key={`owner-${selectedOwner}`}
      label={t('catalog.filters.owner')}
      options={options}
      defaultValue={selectedOwner}
      searchable
      onChange={value => setSelectedOwner(value as string)}
    />
  );
};

const SearchFilter = () => {
  const { queryParameters, updateFilters } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);

  const initialSearch = React.useMemo(
    () => [queryParameters.text].flat().filter(Boolean)[0] as string | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // only on mount
  );
  const [searchText, setSearchText] = React.useState(initialSearch ?? '');

  // Debounce search → EntityListProvider (URL sync handled by provider)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      updateFilters({
        text: searchText ? new EntityTextFilter(searchText) : undefined,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, updateFilters]);

  return (
    <TextField
      className={styles.searchInput}
      placeholder={t('catalog.filters.searchPlaceholder')}
      value={searchText}
      onChange={setSearchText}
      icon={<RiSearchLine size={18} />}
    />
  );
};

// ============================================================================
// Table Content
// ============================================================================

interface EntityTableItem {
  id: string;
  entity: Entity;
  name: string;
  kind: string;
  type: string;
  owner: string;
  lifecycle: string;
  description: string;
}

const EntityTableContent = () => {
  const {
    entities,
    loading,
    totalItems,
    limit,
    setLimit,
    offset,
    setOffset,
  } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);

  const tableData: EntityTableItem[] = React.useMemo(() => {
    return entities.map(entity => {
      const rawOwner = entity.spec?.owner as string | undefined;
      const ownerName = rawOwner
        ? parseEntityRef(rawOwner, { defaultKind: 'group', defaultNamespace: 'default' }).name
        : '-';
      return {
        id: `${entity.metadata.namespace || 'default'}-${entity.kind}-${entity.metadata.name}`,
        entity,
        name: entity.metadata.title || entity.metadata.name,
        kind: entity.kind,
        type: (entity.spec?.type as string) || '-',
        owner: ownerName,
        lifecycle: (entity.spec?.lifecycle as string) || '-',
        description: entity.metadata.description || '-',
      };
    });
  }, [entities]);

  const columnConfig: ColumnConfig<EntityTableItem>[] = [
    {
      id: 'name',
      label: t('catalog.table.name'),
      isRowHeader: true,
      cell: item => <CellText title={item.name} />,
    },
    {
      id: 'kind',
      label: t('catalog.table.kind'),
      cell: item => <CellText title={item.kind} />,
    },
    {
      id: 'type',
      label: t('catalog.table.type'),
      cell: item => <CellText title={item.type} />,
    },
    {
      id: 'owner',
      label: t('catalog.table.owner'),
      cell: item => <CellText title={item.owner} />,
    },
    {
      id: 'lifecycle',
      label: t('catalog.table.lifecycle'),
      cell: item => <CellText title={item.lifecycle} />,
    },
    {
      id: 'description',
      label: t('catalog.table.description'),
      cell: item => {
        const desc = item.description;
        const truncated = desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
        return <CellText title={truncated} />;
      },
    },
  ];

  const rowConfig = {
    getHref: (item: EntityTableItem) => {
      const params = entityRouteParams(item.entity);
      return `/catalog/${params.namespace}/${params.kind}/${params.name}`;
    },
  };

  const currentOffset = offset ?? 0;
  const total = totalItems ?? 0;
  const hasNextPage = currentOffset + limit < total;
  const hasPreviousPage = currentOffset > 0;

  const pageSizeOptions = React.useMemo(() => [
    { label: `${t('catalog.pagination.show')} 5 ${t('catalog.pagination.results')}`, value: 5 },
    { label: `${t('catalog.pagination.show')} 10 ${t('catalog.pagination.results')}`, value: 10 },
    { label: `${t('catalog.pagination.show')} 20 ${t('catalog.pagination.results')}`, value: 20 },
    { label: `${t('catalog.pagination.show')} 50 ${t('catalog.pagination.results')}`, value: 50 },
  ], [t]);

  const getPaginationLabel = React.useCallback(
    ({ offset: labelOffset, totalCount }: { pageSize: number; offset?: number; totalCount?: number }) => {
      if (labelOffset !== undefined && totalCount !== undefined) {
        const from = labelOffset + 1;
        const to = Math.min(labelOffset + limit, totalCount);
        return `${from} - ${to} ${t('catalog.pagination.of')} ${totalCount}`;
      }
      return `${totalCount ?? 0} ${t('catalog.pagination.items')}`;
    },
    [t, limit],
  );

  return (
    <Box className={styles.flexColumnScroll}>
      <Box className={styles.tableContainer}>
        <Table
          columnConfig={columnConfig}
          data={tableData}
          rowConfig={rowConfig}
          loading={loading}
          pagination={{ type: 'none' }}
          className={styles.table}
        />
        <TablePagination
          pageSize={limit}
          totalCount={totalItems}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={() => setOffset?.(currentOffset + limit)}
          onPreviousPage={() => setOffset?.(Math.max(0, currentOffset - limit))}
          onPageSizeChange={(newLimit) => {
            setLimit(newLimit);
            setOffset?.(0);
          }}
          pageSizeOptions={pageSizeOptions}
          getLabel={getPaginationLabel}
          showPageSizeOptions
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// Page Component
// ============================================================================

const CatalogPageComponent = () => {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <Box className={styles.pageShell}>
      <HeaderPage title={t('catalog.pageTitle')} />
      <Content stretch>
        <EntityListProvider pagination={{ mode: 'offset', limit: 20 }}>
          <Flex direction="column" gap="4">
            {/* Filters toolbar */}
            <Flex gap="3" align="end" className={styles.filtersToolbar}>
              <KindFilter />
              <TypeFilter />
              <OwnerFilter />
              <Box className={styles.searchWrapper}>
                <SearchFilter />
              </Box>
            </Flex>

            {/* Table content */}
            <EntityTableContent />
          </Flex>
        </EntityListProvider>
      </Content>
    </Box>
  );
};

// Attach the catalogIndex routeRef so EntityLayout can resolve the "back to catalog" link
attachComponentData(CatalogPageComponent, 'core.mountPoint', catalogPlugin.routes.catalogIndex);

export const CatalogPage = CatalogPageComponent;
